import React, { useState } from "react";
import { injectIntl } from "react-intl";
import cx from 'classnames';
import moment from 'moment';
import withStyles from "isomorphic-style-loader/withStyles";
import { useMutation } from "@apollo/client";
import { useDispatch, useSelector } from "react-redux";
import { Container } from "react-bootstrap";

import Link from "../../components/Link";
import showToaster from "../../helpers/toastrMessgaes/showToaster";
import { formatLocale, isRTL } from "../../helpers/formatLocale";
import { updateProfileSettings } from "../../lib/graphql";
import { choseToCurrency } from "../../actions/currency/getCurrencyRates";
import { choseToTheme } from "../../actions/currency/getCurrencyRates";
import { setLocale } from "../../actions/intl";
import messages from "../../locale/messages";
import bt from '../../components/commonStyle.css';
import s from "./Settings.css";

// Resolve the theme that should actually be applied to the UI.
// We persist the literal selection (light/dark/auto), but "auto" follows the OS preference.
const resolveAppliedTheme = (theme) => {
    if (theme === 'auto') {
        if (typeof window !== 'undefined' && window.matchMedia) {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        return 'light';
    }
    return theme;
};

const Settings = (props) => {
    const { intl: { formatMessage } } = props;

    const dispatch = useDispatch();

    const currencyList = useSelector(state => state.currency.availableCurrencies);
    const currentCurrency = useSelector(state => state.currency.to);
    const currentLocale = useSelector(state => state.intl.locale);
    const availableLocales = useSelector(state => state.runtime.availableLocales);
    const selectedTheme = useSelector(state => state.currency.theme);
    // The persisted profile theme (light/dark/auto). Falls back to the live UI theme.
    const profileTheme = useSelector(state => state?.userAccount?.data?.appTheme);

    const [theme, setTheme] = useState(profileTheme || selectedTheme || 'light');
    const [saving, setSaving] = useState(false);

    const [updateSettings] = useMutation(updateProfileSettings);

    const currencyOptions = (currencyList && currencyList.length > 0)
        ? currencyList.filter(item => item?.isEnable).map(item => item?.symbol)
        : [];

    const themeOptions = [
        { value: 'light', label: formatMessage(messages.themeLight) },
        { value: 'dark', label: formatMessage(messages.themeDark) },
        { value: 'auto', label: formatMessage(messages.themeAuto) }
    ];

    const persist = async (variables) => {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            showToaster({ messageId: 'networkFailed', toasterType: 'error' });
            return false;
        }
        setSaving(true);
        try {
            const { data } = await updateSettings({ variables });
            setSaving(false);
            if (data?.updateProfileSettings?.status === 200) {
                showToaster({ messageId: 'errorMessage', requestContent: formatMessage(messages.settingsSaved), toasterType: 'success' });
                return true;
            }
            showToaster({ messageId: 'errorMessage', requestContent: data?.updateProfileSettings?.errorMessage, toasterType: 'error' });
            return false;
        } catch (error) {
            setSaving(false);
            showToaster({ messageId: 'errorMessage', requestContent: error?.message, toasterType: 'error' });
            return false;
        }
    };

    // Currency: reuse choseToCurrency (same as the header/profile flow) + persist to profile.
    const handleCurrencyChange = async (value) => {
        if (!value || value === currentCurrency) return;
        dispatch(choseToCurrency(value));
        await persist({ preferredCurrency: value });
    };

    // Language: reuse setLocale (same action LanguageModal dispatches) so the UI re-localizes + persist.
    const handleLanguageChange = async (value) => {
        if (!value || value === currentLocale) return;

        const isRtlLocale = isRTL(value);
        if (typeof document !== 'undefined') {
            if (isRtlLocale) {
                document.body.classList.add('rtl');
            } else if (isRTL(currentLocale) && !isRtlLocale) {
                document.body.className = document.body.className.replace(/\brtl\b/g, '');
            }
        }

        dispatch(setLocale({ locale: value }));

        moment.defineLocale(`${value}-dup`, {
            parentLocale: isRtlLocale ? value : 'en',
            preparse: string => string,
            postformat: string => string,
        });

        await persist({ preferredLanguage: value });
    };

    // Theme: reuse choseToTheme (same action the header dispatches) so the UI theme changes + persist.
    const handleThemeChange = async (value) => {
        if (!value || value === theme) return;
        setTheme(value);
        dispatch(choseToTheme(resolveAppliedTheme(value)));
        await persist({ appTheme: value });
    };

    return (
        <div className={cx('subPageContainer')}>
            <Container>
                <div className='subPageWrapper'>
                    <p className={s.pageTitle}>{formatMessage(messages.settings)}</p>

                    <div className={s.settingCard}>
                        <label className={bt.commonLabelText}>{formatMessage(messages.preferredCurrency)}</label>
                        <select
                            className={cx(bt.formControlSelect, 'form-control')}
                            value={currentCurrency || ''}
                            disabled={saving}
                            onChange={(e) => handleCurrencyChange(e.target.value)}
                        >
                            {currencyOptions.map((symbol) => (
                                <option key={symbol} value={symbol}>{symbol}</option>
                            ))}
                        </select>
                    </div>

                    <div className={s.settingCard}>
                        <label className={bt.commonLabelText}>{formatMessage(messages.language)}</label>
                        <select
                            className={cx(bt.formControlSelect, 'form-control')}
                            value={currentLocale || ''}
                            disabled={saving}
                            onChange={(e) => handleLanguageChange(e.target.value)}
                        >
                            {(availableLocales || []).map((locale) => (
                                <option key={locale} value={locale}>{formatLocale(locale)}</option>
                            ))}
                        </select>
                    </div>

                    <div className={s.settingCard}>
                        <label className={bt.commonLabelText}>{formatMessage(messages.theme)}</label>
                        <div className={s.themeSelector}>
                            {themeOptions.map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    disabled={saving}
                                    className={cx(s.themePill, { [s.themePillActive]: theme === opt.value })}
                                    onClick={() => handleThemeChange(opt.value)}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <Link to="/saved-addresses" className={s.linkRow}>
                        <span className={s.linkRowLabel}>{formatMessage(messages.savedAddresses)}</span>
                        <span className={s.linkRowChevron}>›</span>
                    </Link>

                    <Link to="/senior-veteran-discount" className={s.linkRow}>
                        <span className={s.linkRowLabel}>Senior &amp; Veteran Discount</span>
                        <span className={s.linkRowChevron}>›</span>
                    </Link>
                </div>
            </Container>
        </div>
    );
};

export default injectIntl(withStyles(bt, s)(Settings));
